import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { CommonConstant } from '@utility/constants/common';
import CatalogService from '@utility/services/catalog';
import { catalogMappingKeys } from '../lib/query-keys';
import type { DuplicateGroup, DuplicateListingsParams } from '../lib/types';
import { ALL } from '../lib/unmatched-params';

const noGroups: DuplicateGroup[] = [];

type DuplicateFilter = {
  page: number;
  limit: number;
  search: string;
  regionId: string;
};

/* Catalog items whose listings disagree on title or SKU, to be merged. */
export function useDuplicateListings(initialSearch = '') {
  const [filter, setFilter] = useState<DuplicateFilter>({
    page: 1,
    limit: CommonConstant.defaultPageSize,
    search: initialSearch,
    regionId: ALL,
  });

  const params: DuplicateListingsParams = {
    page: filter.page,
    limit: filter.limit,
    search: filter.search.trim(),
    ...(filter.regionId === ALL
      ? {}
      : { marketplace_region_id: filter.regionId }),
  };
  /* Typing in the search box asks once it settles, not on every key. */
  const applied = useDebouncedMemo<DuplicateListingsParams>(
    () => params,
    [JSON.stringify(params)],
    500,
  );

  const listQuery = useQuery({
    queryKey: catalogMappingKeys.duplicates(applied ?? params),
    queryFn: async (): Promise<DuplicateGroup[]> =>
      (await CatalogService.mergeListingData(applied)).data ?? [],
    enabled: !!applied,
    placeholderData: keepPreviousData,
  });

  const pageQuery = useQuery({
    queryKey: catalogMappingKeys.duplicatePages(applied ?? params),
    queryFn: async (): Promise<{ total_data?: number }> =>
      (await CatalogService.mergePageData(applied)).data,
    enabled: !!applied,
    placeholderData: keepPreviousData,
  });

  /* Any filter but the page starts the list over. */
  const filterChanged = <K extends keyof DuplicateFilter>(
    key: K,
    value: DuplicateFilter[K],
  ) => {
    setFilter(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));
  };

  return {
    data: listQuery.data ?? noGroups,
    loading: !applied || listQuery.isPending,
    totalData: pageQuery.data?.total_data ?? 0,
    filter,
    filterChanged,
    refetch: () => {
      listQuery.refetch();
      pageQuery.refetch();
    },
  };
}
